import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "cube-tumble";

const DEFAULT_BG = "#0b1024";
const DEFAULT_NEAR = "#22d3ee";
const DEFAULT_FAR = "#7c3aed";

const CUBE_DENSITY = 0.0000028; // cubes per pixel² (sparse — they're each fairly large)
const MIN_CUBES = 5;
const MAX_CUBES = 18;
const CUBE_HALF = 36;            // half edge length in world units
const CAMERA_DEPTH = 600;        // perspective focal length
const Z_NEAR = 80;
const Z_FAR = 1100;

// 8 cube vertices in object-local coords, edges as vertex-index pairs.
const CUBE_VERTS = [
  [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
  [-1, -1, 1],  [1, -1, 1],  [1, 1, 1],  [-1, 1, 1],
];
const CUBE_EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 0],   // back face
  [4, 5], [5, 6], [6, 7], [7, 4],   // front face
  [0, 4], [1, 5], [2, 6], [3, 7],   // connectors
];

function rand(min, max) { return min + Math.random() * (max - min); }

// Lerp a hex color toward another by k. Returns rgba string.
function blendHex(hexA, hexB, k, alpha) {
  // Cheap inline parse; keeps this file standalone-ish.
  const a = parseInt(hexA.replace("#", ""), 16);
  const b = parseInt(hexB.replace("#", ""), 16);
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = ar + (br - ar) * k;
  const g = ag + (bg - ag) * k;
  const bv = ab + (bb - ab) * k;
  return `rgba(${r | 0}, ${g | 0}, ${bv | 0}, ${alpha})`;
}

export default function CubeTumble({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const cubesRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_NEAR, DEFAULT_FAR]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_NEAR),
      pickColor(colors, 2, DEFAULT_FAR),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedCubes = () => {
      const target = Math.round(width * height * CUBE_DENSITY);
      const count = Math.max(MIN_CUBES, Math.min(MAX_CUBES, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = {
          // World position; x/y span the visible frustum, z spans Z_NEAR..Z_FAR.
          x: rand(-width * 0.6, width * 0.6),
          y: rand(-height * 0.6, height * 0.6),
          z: rand(Z_NEAR, Z_FAR),
          // Independent rotation rates per axis → each cube tumbles in its
          // own way, no two look identical.
          rx: 0,
          ry: 0,
          rz: 0,
          drx: rand(-0.012, 0.012),
          dry: rand(-0.012, 0.012),
          drz: rand(-0.008, 0.008),
          // Slow drift through space; also a tiny depth oscillation so the
          // perspective scaling pulses.
          vx: rand(-0.18, 0.18),
          vy: rand(-0.18, 0.18),
          vz: rand(-0.20, 0.20),
          scale: rand(0.7, 1.4) * CUBE_HALF,
        };
      }
      cubesRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedCubes();
    };

    // Project a point from cube-local → world → screen with a simple
    // pinhole camera at (0,0,0) looking +z. Vertices are first rotated by
    // the cube's own (rx, ry, rz), translated by its world position, then
    // perspective-divided.
    const projectVertex = (vx, vy, vz, cube, halfW, halfH) => {
      // Rotate around X.
      let y = vy * Math.cos(cube.rx) - vz * Math.sin(cube.rx);
      let z = vy * Math.sin(cube.rx) + vz * Math.cos(cube.rx);
      let x = vx;
      // Rotate around Y.
      let nx = x * Math.cos(cube.ry) + z * Math.sin(cube.ry);
      let nz = -x * Math.sin(cube.ry) + z * Math.cos(cube.ry);
      x = nx;
      z = nz;
      // Rotate around Z.
      nx = x * Math.cos(cube.rz) - y * Math.sin(cube.rz);
      let ny = x * Math.sin(cube.rz) + y * Math.cos(cube.rz);
      x = nx;
      y = ny;

      const wx = cube.x + x * cube.scale;
      const wy = cube.y + y * cube.scale;
      const wz = cube.z + z * cube.scale;

      const k = CAMERA_DEPTH / Math.max(1, wz);
      return { sx: halfW + wx * k, sy: halfH + wy * k, wz };
    };

    const tick = () => {
      const [bg, near, far] = colorsRef.current;

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const halfW = width / 2;
      const halfH = height / 2;

      const cubes = cubesRef.current;
      // Sort back-to-front by mean z so closer cubes draw on top.
      cubes.sort((a, b) => b.z - a.z);

      for (let i = 0; i < cubes.length; i += 1) {
        const c = cubes[i];
        c.rx += c.drx;
        c.ry += c.dry;
        c.rz += c.drz;
        c.x += c.vx;
        c.y += c.vy;
        c.z += c.vz;

        // Wrap depth — once a cube passes the camera or recedes too far,
        // re-spawn it on the opposite z boundary at a new x/y so the field
        // never empties out.
        if (c.z < Z_NEAR) {
          c.z = Z_FAR;
          c.x = rand(-width * 0.6, width * 0.6);
          c.y = rand(-height * 0.6, height * 0.6);
        } else if (c.z > Z_FAR) {
          c.z = Z_NEAR;
          c.x = rand(-width * 0.6, width * 0.6);
          c.y = rand(-height * 0.6, height * 0.6);
        }

        // Project all 8 vertices once.
        const projected = new Array(8);
        for (let v = 0; v < 8; v += 1) {
          projected[v] = projectVertex(
            CUBE_VERTS[v][0], CUBE_VERTS[v][1], CUBE_VERTS[v][2],
            c, halfW, halfH
          );
        }

        // Color + alpha based on depth: close cubes lean "near", far cubes
        // lean "far", and far cubes also fade out so they recede naturally.
        const depthT = (c.z - Z_NEAR) / (Z_FAR - Z_NEAR);
        const alpha = (1 - depthT) * 0.85 + 0.1;

        ctx.lineCap = "round";
        ctx.lineWidth = 1.5 * Math.max(0.4, 1 - depthT * 0.7);

        for (let e = 0; e < CUBE_EDGES.length; e += 1) {
          const [a, b] = CUBE_EDGES[e];
          const pa = projected[a];
          const pb = projected[b];
          // Per-edge depth → blend between near and far colors.
          const meanZ = (pa.wz + pb.wz) * 0.5;
          const edgeT = Math.max(0, Math.min(1, (meanZ - Z_NEAR) / (Z_FAR - Z_NEAR)));
          ctx.strokeStyle = blendHex(near, far, edgeT, alpha);
          ctx.beginPath();
          ctx.moveTo(pa.sx, pa.sy);
          ctx.lineTo(pb.sx, pb.sy);
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
